import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Skeleton, TextField, Typography } from "@mui/material";
import { FormikErrors, useFormik } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import * as Sentry from "@sentry/react";
import {
  AccordionCard,
  Callout,
  Checkbox,
  ChoiceToggle,
  CountrySelect,
  FieldWrapper,
  PageHeader,
  PepPersonFields,
  PepPersonValue,
  StepActions,
  TabBofuV2,
} from "@components/ui_v2";
import useDebouncedEffect from "@hooks/useDebouncedEffect";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetCuentasByIdQuery, usePatchCuentaMutation } from "@api/account";
import {
  useLazyGetPaymentDetailsQuery,
  useLazyGetPaymentMethodByIdQuery,
  usePostPaymentDetailsMutation,
  usePostPaymentMethodMutation,
  usePutApplicationCompletedMutation,
  usePutPaymentDetailsMutation,
} from "@api/applications";
import {
  useLazyGetCargoPublicoQuery,
  useLazyGetDependeciasQuery,
  useLazyGetPaymentMethodQuery,
  useLazyGetPepsTypeQuery,
  useLazyGetRelationshipQuery,
} from "@api/catalogs";
import {
  useDeletePepsMutation,
  useLazyGetPepsByPersonQuery,
  usePatchPepsDataMutation,
  usePotsPepsDataMutation,
} from "@api/pepsApi";
import { CargoPublicoData, DependenciasData, PaymentMethod, RelationshipData } from "@interfaces/catalogs";
import { ApiError, PayLoadPeps, PepsData } from "@interfaces/response";
import { PATHS_EXPEDIENTE_V2, PAYMENT_METHOD_CATALOG, PEP_TYPE_CATALOG } from "@helpers/constantes";
import { separarNombres } from "@helpers/funciones";
import { PHONE_NUMBER } from "@utils/expreciones";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const PATH_EXPEDIENTE = PATHS_EXPEDIENTE_V2.empresa.path;
const PATH_REFERENCIAS = PATHS_EXPEDIENTE_V2.referencias.path;

/** Tope de personas por declaración, igual que el expediente legacy. */
const MAX_PEPS = 5;

const emptyPep = (): PepPersonValue => ({
  parentesco: "",
  nombre: "",
  ap_paterno: "",
  ap_materno: "",
  dependencia: "",
  cargo: "",
  actualmente: "",
  start_date: "",
  end_date: "",
  id: "",
});

/**
 * Qué campos de formik le tocan a cada sección. El guardado parcial los usa para dos cosas: saber
 * si la sección cambió respecto a lo ya persistido, y si está completa y válida para enviarse.
 */
const CAMPOS_SECCION = {
  contacto: ["country_code", "cell_phone_number"],
  trans: ["paymentMethods", "otherPaymentMethod", "advancePayments", "resourcesTypes"],
  pep: ["socioFuncionario", "conyuge", "peps_socio", "peps_conyuge"],
} as const;

type Seccion = keyof typeof CAMPOS_SECCION;

/** Espera a que el usuario deje de escribir antes de guardar el teléfono. */
const MS_DEBOUNCE = 1500;

/** Alto del skeleton que cubre el formulario mientras carga; aproxima los tres acordeones. */
const ALTO_SKELETON = 420;

/** Firma de los valores de una sección, para comparar contra lo último que se guardó. */
const firmaSeccion = (valores: Record<string, unknown>, seccion: Seccion) =>
  JSON.stringify(CAMPOS_SECCION[seccion].map((campo) => valores[campo]));

/**
 * El backend guarda el nombre completo en un solo campo; `separarNombres` asume que las dos últimas
 * palabras son los apellidos, y aquí se renombran a los campos que usa el formulario.
 */
const pepToValue = (item: PepsData): PepPersonValue => {
  const { nombre, apellido1, apellido2 } = separarNombres(item.name.trim());

  return {
    parentesco: item.relationship_id || "",
    nombre,
    ap_paterno: apellido1,
    ap_materno: apellido2,
    dependencia: item.dependency,
    cargo: item.position,
    actualmente: item.active ? "true" : "false",
    start_date: dayjs(item.start_date).format("YYYY-MM-DD"),
    end_date: item.end_date ? dayjs(item.end_date).format("YYYY-MM-DD") : "",
    id: item.id,
  };
};

/**
 * Esquema de una persona PEP. `contextKey` es la respuesta Sí/No que la condiciona: formik pasa los
 * `values` como contexto de yup, así que `$socioFuncionario` / `$conyuge` se resuelven desde ahí.
 */
const pepItemSchema = (contextKey: "socioFuncionario" | "conyuge", requireParentesco: boolean) => {
  const requiredWhenYes = (schema: yup.StringSchema) =>
    schema.when(`$${contextKey}`, ([respuesta], current: yup.StringSchema) =>
      respuesta === "true" ? current.required("Campo requerido") : current.notRequired(),
    );

  return yup.object().shape({
    parentesco: requireParentesco ? requiredWhenYes(yup.string()) : yup.string().notRequired(),
    nombre: requiredWhenYes(yup.string()),
    ap_paterno: requiredWhenYes(yup.string()),
    ap_materno: requiredWhenYes(yup.string()),
    dependencia: requiredWhenYes(yup.string()),
    cargo: requiredWhenYes(yup.string()),
    actualmente: requiredWhenYes(yup.string()),
    start_date: requiredWhenYes(
      yup
        .string()
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
        .test("start_date_not_in_future", "La fecha de inicio no puede ser mayor al día actual", (value) =>
          value ? dayjs(value).isSameOrBefore(dayjs(), "day") : true,
        ),
    ),
    end_date: yup
      .string()
      .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
      .when("actualmente", {
        is: "false",
        then: (schema) =>
          schema
            .required("Campo requerido")
            .test(
              "end_date_greater_than_start_date",
              "La fecha de término no puede ser menor que la fecha de inicio",
              function (value) {
                const { start_date } = this.parent;
                return start_date && value ? dayjs(value).isSameOrAfter(dayjs(start_date), "day") : true;
              },
            )
            .test("end_date_not_in_future", "La fecha de término no puede ser mayor al día actual", (value) =>
              value ? dayjs(value).isSameOrBefore(dayjs(), "day") : true,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
};

export const ExpInformacionSolicitud = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const bofuLoading = useSelector((state: RootState) => state.bofu.isLoading);

  const [getCuentaById] = useLazyGetCuentasByIdQuery();
  const [patchCuenta] = usePatchCuentaMutation();
  const [getPaymentDetails] = useLazyGetPaymentDetailsQuery();
  const [getPaymentMethodById] = useLazyGetPaymentMethodByIdQuery();
  const [getPaymentMethodCatalog] = useLazyGetPaymentMethodQuery();
  const [postPaymentMethod] = usePostPaymentMethodMutation();
  const [postPaymentDetails] = usePostPaymentDetailsMutation();
  const [putPaymentDetails] = usePutPaymentDetailsMutation();
  const [putApplicationCompleted] = usePutApplicationCompletedMutation();
  const [getPepsType] = useLazyGetPepsTypeQuery();
  const [getPepsByPerson] = useLazyGetPepsByPersonQuery();
  const [getDependencias] = useLazyGetDependeciasQuery();
  const [getCargoPublico] = useLazyGetCargoPublicoQuery();
  const [getRelationship] = useLazyGetRelationshipQuery();
  const [postPeps] = usePotsPepsDataMutation();
  const [patchPepsData] = usePatchPepsDataMutation();
  const [deletePeps] = useDeletePepsMutation();

  const [isLoading, setIsLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState<Set<number>>(new Set([0, 1, 2]));

  const [paymentMethodList, setPaymentMethodList] = useState<PaymentMethod[]>([]);
  const [idOtherPayment, setIdOtherPayment] = useState("");
  const [idDetalle, setIdDetalle] = useState<string | null>(null);
  const [pepsIdSocio, setPepsIdSocio] = useState("");
  const [pepsIdFamiliar, setPepsIdFamiliar] = useState("");
  const [listDependencias, setListDependencias] = useState<DependenciasData[]>([]);
  const [listCargos, setListCargos] = useState<CargoPublicoData[]>([]);
  const [listParentescos, setListParentescos] = useState<RelationshipData[]>([]);

  const [contactoDone, setContactoDone] = useState(false);
  const [transDone, setTransDone] = useState(false);
  const [pepDone, setPepDone] = useState(false);

  const formik = useFormik({
    initialValues: {
      // Datos de contacto de la empresa
      country_code: "+52",
      cell_phone_number: "",
      // Información transaccional
      paymentMethods: [] as string[],
      otherPaymentMethod: "",
      otro: false,
      advancePayments: "",
      resourcesTypes: "",
      // Declaraciones regulatorias (PEP)
      socioFuncionario: "",
      conyuge: "",
      peps_socio: [emptyPep()],
      peps_conyuge: [emptyPep()],
    },
    validationSchema: yup.object().shape({
      country_code: yup.string().required("Campo requerido"),
      cell_phone_number: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
      paymentMethods: yup.array().min(1, "Selecciona al menos una opción").of(yup.string().required()).required(),
      otro: yup.boolean(),
      otherPaymentMethod: yup.string().when("otro", {
        is: true,
        then: (schema) => schema.required("Este campo es requerido cuando la opción otro esta seleccionada"),
        otherwise: (schema) => schema.notRequired(),
      }),
      advancePayments: yup.string().required("Campo requerido"),
      resourcesTypes: yup.string().required("Campo requerido"),
      socioFuncionario: yup.string().required("Campo requerido"),
      conyuge: yup.string().required("Campo requerido"),
      peps_socio: yup.array().of(pepItemSchema("socioFuncionario", false)),
      peps_conyuge: yup.array().of(pepItemSchema("conyuge", true)),
    }),
    // El envío de la pantalla es "Continuar": cada acordeón persiste lo suyo con su propio Guardar.
    onSubmit: async (_values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        await putApplicationCompleted({
          id_sol: application.id,
          body: { loan_application_information_status: "COMPLETO" },
        }).unwrap();

        navigate(PATH_REFERENCIAS);
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "ExpInformacionSolicitud-submit" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue, setValues, validateForm } = formik;

  /**
   * Carga inicial: cuenta (teléfono), detalles y métodos de pago de la solicitud, los catálogos de
   * las declaraciones PEP y las personas ya declaradas. Los detalles de pago se consultan aparte
   * porque el endpoint responde error cuando la solicitud todavía no tiene ninguno.
   */
  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);

      const detallesWrapped = async () => {
        try {
          return await getPaymentDetails({ solicitudId: application.id }).unwrap();
        } catch {
          return null;
        }
      };

      const [cuenta, detallesResponse, metodosResponse, catalogoPagos, tiposPeps, dependencias, cargos, parentescos] =
        await Promise.all([
          getCuentaById({ id: application.client_details_id }).unwrap(),
          detallesWrapped(),
          getPaymentMethodById({ solicitudId: application.id }).unwrap(),
          getPaymentMethodCatalog().unwrap(),
          getPepsType().unwrap(),
          getDependencias().unwrap(),
          getCargoPublico().unwrap(),
          getRelationship().unwrap(),
        ]);

      const detalles = detallesResponse?.data;
      const metodos = metodosResponse.data;
      const otroPayment = catalogoPagos.find((item) => item.code === PAYMENT_METHOD_CATALOG.otro.code);
      const metodoOtro = metodos?.find((item) => item.payment_method?.id === otroPayment?.id);
      const idSocio = String(tiposPeps.find((item) => item.name === PEP_TYPE_CATALOG.socio.name)?.id || "");
      const idFamiliar = String(tiposPeps.find((item) => item.name === PEP_TYPE_CATALOG.familiar.name)?.id || "");

      const pepsPersona = idSocio
        ? await getPepsByPerson({ personId: application.client_details_id, pepsId: idSocio }).unwrap()
        : [];
      const pepsFamiliares = idFamiliar
        ? await getPepsByPerson({ personId: application.client_details_id, pepsId: idFamiliar }).unwrap()
        : [];

      const socios = pepsPersona.filter((item) => item.pep_type_id === idSocio).map(pepToValue);
      const familiares = pepsFamiliares.filter((item) => item.pep_type_id === idFamiliar).map(pepToValue);

      setPaymentMethodList(catalogoPagos);
      setIdOtherPayment(otroPayment?.id || "");
      setIdDetalle(detalles?.id ?? null);
      setPepsIdSocio(idSocio);
      setPepsIdFamiliar(idFamiliar);
      setListDependencias(dependencias);
      setListCargos(cargos);
      setListParentescos(parentescos);

      const metodoIds = metodos?.map((item) => item.payment_method?.id) ?? [];

      setValues((current) => {
        const cargados = {
          ...current,
          country_code: cuenta.data.country_code || current.country_code,
          cell_phone_number: cuenta.data.cell_phone_number || "",
          paymentMethods: metodoIds,
          otherPaymentMethod: metodoOtro?.other_payment_method || "",
          otro: metodoIds.includes(otroPayment?.id || ""),
          advancePayments: detalles ? (detalles.advance_payments ? "Si" : "No") : "",
          resourcesTypes: detalles ? (detalles.own_resources ? "Propios" : "Tercero") : "",
          // El legacy escribía el string "undefined" cuando el backend aún no tenía la respuesta;
          // aquí se deja vacío para que el toggle no arranque con una opción inexistente.
          socioFuncionario: typeof detalles?.pep_shareholder === "boolean" ? String(detalles.pep_shareholder) : "",
          conyuge: typeof detalles?.pep_members === "boolean" ? String(detalles.pep_members) : "",
          peps_socio: socios.length ? socios : [emptyPep()],
          peps_conyuge: familiares.length ? familiares : [emptyPep()],
        };

        // Firma de lo que ya está en backend: el guardado parcial solo dispara cuando los valores
        // de una sección se apartan de aquí, para no reenviar lo que se acaba de cargar.
        guardadoRef.current = {
          contacto: firmaSeccion(cargados, "contacto"),
          trans: firmaSeccion(cargados, "trans"),
          pep: firmaSeccion(cargados, "pep"),
        };
        return cargados;
      });

      setContactoDone(Boolean(cuenta.data.cell_phone_number));
      setTransDone(Boolean(detalles) && metodoIds.length > 0);
      setPepDone(typeof detalles?.pep_shareholder === "boolean" && typeof detalles?.pep_members === "boolean");

      setTimeout(() => validateForm(), 200);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpInformacionSolicitud-initial_fetch" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  }, [
    application.id,
    application.client_details_id,
    getCuentaById,
    getPaymentDetails,
    getPaymentMethodById,
    getPaymentMethodCatalog,
    getPepsType,
    getPepsByPerson,
    getDependencias,
    getCargoPublico,
    getRelationship,
    setValues,
    validateForm,
    dispatch,
  ]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const toggle = (index: number) =>
    setOpenIdx((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });

  /** Guarda o crea los detalles de pago de la solicitud y recuerda su id. */
  const savePaymentDetails = async (body: {
    advance_payments?: boolean;
    own_account?: boolean;
    own_resources?: boolean;
    pep_shareholder?: boolean;
    pep_members?: boolean;
  }) => {
    if (idDetalle) {
      await putPaymentDetails({ solicitudId: application.id, detailId: idDetalle, body }).unwrap();
      return;
    }

    // El legacy solo hacía el PUT y perdía las banderas PEP cuando el detalle todavía no existía
    // (en tres pantallas encadenadas siempre existía). Aquí los tres bloques son independientes,
    // así que el primero que guarde crea el registro.
    const nuevo = await postPaymentDetails({ solicitudId: application.id, body }).unwrap();
    setIdDetalle(nuevo.data.id);
  };

  const guardarContacto = async () => {
    try {
      await patchCuenta({
        accountId: application.client_details_id,
        body: {
          country_code: formik.values.country_code,
          cell_phone_number: formik.values.cell_phone_number,
        },
      }).unwrap();

      setContactoDone(true);
      return true;
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpInformacionSolicitud-guardarContacto" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      return false;
    }
  };

  const guardarTransaccional = async () => {
    try {
      await postPaymentMethod({
        solicitudId: application.id,
        body: {
          payment_methods: formik.values.paymentMethods.map((id) => ({
            payment_method_id: id,
            other_payment_method: id === idOtherPayment ? formik.values.otherPaymentMethod : null,
          })),
        },
      }).unwrap();

      await savePaymentDetails({
        advance_payments: formik.values.advancePayments === "Si",
        own_account: false,
        own_resources: formik.values.resourcesTypes === "Propios",
      });

      setTransDone(true);
      return true;
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpInformacionSolicitud-guardarTransaccional" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      return false;
    }
  };

  /** Crea, actualiza o borra las personas de una declaración según se haya respondido Sí o No. */
  const syncPeps = async (
    personas: PepPersonValue[],
    declarado: boolean,
    pepTypeId: string,
    conParentesco: boolean,
  ) => {
    if (!declarado) {
      for (const persona of personas) {
        if (persona.id) {
          await deletePeps({ id: persona.id }).unwrap();
        }
      }
      return;
    }

    for (const persona of personas) {
      const body: PayLoadPeps = {
        name: `${persona.nombre} ${persona.ap_paterno} ${persona.ap_materno}`,
        position: persona.cargo,
        dependency: persona.dependencia,
        active: persona.actualmente === "true",
        start_date: persona.start_date,
        end_date: persona.end_date === "" ? null : persona.end_date,
        loan_application_id: application.id,
        pep_type_id: pepTypeId,
        client_details_id: application.client_details_id,
        relationship_id: conParentesco ? persona.parentesco : null,
      };

      if (persona.id) {
        await patchPepsData({ id: persona.id, body }).unwrap();
      } else {
        await postPeps(body).unwrap();
      }
    }
  };

  const guardarPep = async () => {
    try {
      const esSocio = formik.values.socioFuncionario === "true";
      const esFamiliar = formik.values.conyuge === "true";

      await savePaymentDetails({ pep_shareholder: esSocio, pep_members: esFamiliar });
      await syncPeps(formik.values.peps_socio, esSocio, pepsIdSocio, false);
      await syncPeps(formik.values.peps_conyuge, esFamiliar, pepsIdFamiliar, true);

      // Los ids locales quedan obsoletos tras crear o borrar registros; se releen del backend.
      await fetchInitialData();

      setPepDone(true);
      return true;
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpInformacionSolicitud-guardarPep" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      return false;
    }
  };

  /*
   * Guardado parcial. En vez de un botón por sección, cada una se persiste sola en cuanto sus
   * campos quedan completos y válidos, como lo plantea `SolSection` en el diseño.
   *
   * Tres guardas, cada una por una razón distinta:
   *   1. La firma contra `guardadoRef` evita reenviar lo que la carga inicial acaba de traer, y
   *      permite volver a guardar si el usuario edita una sección ya resuelta.
   *   2. La validación corre en silencio —sin tocar `touched`— para no pintar errores mientras se
   *      captura; simplemente no se guarda hasta que la sección esté bien.
   *   3. `enVuelo` impide que un cambio durante la petición dispare una segunda en paralelo.
   */
  const guardadoRef = useRef<Record<Seccion, string | null>>({ contacto: null, trans: null, pep: null });
  const enVuelo = useRef<Record<Seccion, boolean>>({ contacto: false, trans: false, pep: false });

  // El efecto solo depende de los valores; las funciones se leen de la ref para no arrastrar media
  // pantalla en sus dependencias ni recrear el efecto en cada render.
  const guardarRef = useRef({ contacto: guardarContacto, trans: guardarTransaccional, pep: guardarPep });
  useEffect(() => {
    guardarRef.current = { contacto: guardarContacto, trans: guardarTransaccional, pep: guardarPep };
  });

  const autoguardar = useCallback(
    async (seccion: Seccion) => {
      const firma = firmaSeccion(formik.values, seccion);
      if (guardadoRef.current[seccion] === null || guardadoRef.current[seccion] === firma) return;
      if (enVuelo.current[seccion]) return;

      const errores = await validateForm();
      if (CAMPOS_SECCION[seccion].some((campo) => campo in errores)) return;

      enVuelo.current[seccion] = true;
      const guardado = await guardarRef.current[seccion]();
      enVuelo.current[seccion] = false;

      if (!guardado) return;

      // La sección no se pliega al guardar: abrir y cerrar los acordeones lo maneja el usuario.
      guardadoRef.current[seccion] = firma;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values, validateForm],
  );

  // El teléfono se escribe letra por letra: se espera a que el usuario termine.
  useDebouncedEffect(() => autoguardar("contacto"), [autoguardar], MS_DEBOUNCE);

  // Las otras dos son opciones discretas: en cuanto quedan respondidas, se guardan.
  useEffect(() => {
    autoguardar("trans");
    autoguardar("pep");
  }, [autoguardar]);

  const togglePaymentMethod = (paymentMethodId: string, checked: boolean) => {
    const seleccionados = checked
      ? [...formik.values.paymentMethods, paymentMethodId]
      : formik.values.paymentMethods.filter((id) => id !== paymentMethodId);

    setFieldValue("paymentMethods", seleccionados);
    setFieldValue("otro", seleccionados.includes(idOtherPayment));
    setTimeout(() => validateForm(), 200);
  };

  const responderDeclaracion = (
    campo: "socioFuncionario" | "conyuge",
    lista: "peps_socio" | "peps_conyuge",
    valor: string,
  ) => {
    setFieldValue(campo, valor);
    // Al responder que sí, siempre debe haber una persona por capturar.
    if (valor === "true" && formik.values[lista].length === 0) {
      setFieldValue(lista, [emptyPep()]);
    }
    setTimeout(() => validateForm(), 200);
  };

  const agregarPersona = (lista: "peps_socio" | "peps_conyuge") => {
    if (formik.values[lista].length >= MAX_PEPS) return;
    setFieldValue(lista, [...formik.values[lista], emptyPep()]);
  };

  const removerPersona = async (
    lista: "peps_socio" | "peps_conyuge",
    campo: "socioFuncionario" | "conyuge",
    index: number,
  ) => {
    try {
      const persona = formik.values[lista][index];
      if (persona.id) {
        await deletePeps({ id: persona.id }).unwrap();
      }

      const restantes = formik.values[lista].filter((_, i) => i !== index);
      setFieldValue(lista, restantes);

      // Sin personas declaradas la respuesta deja de ser "sí", igual que el expediente legacy.
      if (restantes.length === 0) {
        setFieldValue(campo, "false");
      }
      setTimeout(() => validateForm(), 200);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpInformacionSolicitud-removerPersona" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const disabled = isLoading || formik.isSubmitting;
  const SI_NO = [
    { value: "true", label: "Sí" },
    { value: "false", label: "No" },
  ];

  const renderListaPeps = (
    lista: "peps_socio" | "peps_conyuge",
    campo: "socioFuncionario" | "conyuge",
    testIdBase: string,
    parentescos?: RelationshipData[],
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {formik.values[lista].map((persona, index) => (
        <PepPersonFields
          key={persona.id || index}
          namePrefix={`${lista}[${index}]`}
          value={persona}
          touched={formik.touched[lista]?.[index]}
          errors={(formik.errors[lista] as FormikErrors<PepPersonValue>[] | undefined)?.[index]}
          dependencias={listDependencias}
          cargos={listCargos}
          parentescos={parentescos}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          setFieldValue={setFieldValue}
          onRemove={() => removerPersona(lista, campo, index)}
          disabled={disabled}
          testIdPrefix={`${testIdBase}-${index}`}
        />
      ))}

      {formik.values[lista].length < MAX_PEPS && (
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={() => agregarPersona(lista)}
          disabled={disabled}
          data-testid={`${testIdBase}-add-button`}
          sx={{
            alignSelf: "flex-start",
            background: "none",
            border: "none",
            p: 0,
            cursor: "pointer",
            color: "primary.main",
            fontWeight: 500,
            "&:disabled": { cursor: "not-allowed", opacity: 0.5 },
          }}
        >
          + Agregar otro
        </Typography>
      )}
    </Box>
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Expediente digital · Empresa · Información de la solicitud"
        title="Información de la solicitud"
        subtitle="Revisa y completa la información de la solicitud antes de continuar con el expediente."
      />

      <TabBofuV2 active="empresa" />

      {bofuLoading || isLoading ? (
        <Skeleton variant="rectangular" animation="wave" sx={{ height: ALTO_SKELETON, borderRadius: "12px" }} />
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AccordionCard
              index={1}
              title="Datos de contacto de la empresa"
              summary={
                contactoDone ? `${formik.values.country_code} ${formik.values.cell_phone_number}` : "Sin información"
              }
              status={contactoDone ? "COMPLETO" : "PENDIENTE"}
              open={openIdx.has(0)}
              onToggle={() => toggle(0)}
            >
              <FieldWrapper
                label="Teléfono de la empresa"
                full
                error={
                  (formik.touched.country_code && formik.errors.country_code) ||
                  (formik.touched.cell_phone_number && formik.errors.cell_phone_number) ||
                  ""
                }
              >
                {/*
                 * TODO: el diseño incluye un tercer campo "Ext."; `PayloadAccountPatch` no tiene dónde
                 * guardarlo, así que no se renderiza para no capturar un dato que se perdería.
                 */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <CountrySelect
                    value={formik.values.country_code}
                    onChange={(value) => setFieldValue("country_code", value)}
                    disabled={disabled}
                  />
                  <TextField
                    autoComplete="off"
                    size="lendia_input"
                    fullWidth
                    name="cell_phone_number"
                    value={formik.values.cell_phone_number}
                    onChange={(event) => setFieldValue("cell_phone_number", event.target.value.replace(/[^0-9]/g, ""))}
                    onBlur={formik.handleBlur}
                    placeholder="55 1234 5678"
                    disabled={disabled}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                      "data-testid": "expediente-solicitud-telefono-input",
                    }}
                  />
                </Box>
              </FieldWrapper>
            </AccordionCard>

            <AccordionCard
              index={2}
              title="Información transaccional"
              summary={transDone ? "Completado" : "Sin información"}
              status={transDone ? "COMPLETO" : "PENDIENTE"}
              open={openIdx.has(1)}
              onToggle={() => toggle(1)}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FieldWrapper
                  label="¿De qué forma realizarás tus pagos?"
                  full
                  error={(formik.touched.paymentMethods && (formik.errors.paymentMethods as string)) || ""}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {paymentMethodList.map((paymentMethod) => (
                      <Checkbox
                        key={paymentMethod.id}
                        checked={formik.values.paymentMethods.includes(paymentMethod.id)}
                        onChange={(checked) => togglePaymentMethod(paymentMethod.id, checked)}
                        disabled={disabled}
                        label={
                          <Typography
                            variant="body2"
                            color="primary.dark"
                            data-testid={`expediente-solicitud-metodo-pago-${paymentMethod.code}`}
                          >
                            {paymentMethod.description}
                          </Typography>
                        }
                      />
                    ))}
                  </Box>
                </FieldWrapper>

                {formik.values.otro && (
                  <FieldWrapper
                    label="Especifica el método de pago"
                    full
                    error={(formik.touched.otherPaymentMethod && formik.errors.otherPaymentMethod) || ""}
                  >
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      name="otherPaymentMethod"
                      value={formik.values.otherPaymentMethod}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={disabled}
                      inputProps={{ maxLength: 100, "data-testid": "expediente-solicitud-otro-metodo-input" }}
                    />
                  </FieldWrapper>
                )}

                <FieldWrapper
                  label="¿Realizarás pagos anticipados?"
                  full
                  error={(formik.touched.advancePayments && formik.errors.advancePayments) || ""}
                >
                  <ChoiceToggle
                    value={formik.values.advancePayments}
                    onChange={(value) => setFieldValue("advancePayments", String(value))}
                    options={[
                      { value: "Si", label: "Sí" },
                      { value: "No", label: "No" },
                    ]}
                    disabled={disabled}
                    size="small"
                  />
                </FieldWrapper>

                <FieldWrapper
                  label="¿Los recursos son propios o de un tercero?"
                  full
                  error={(formik.touched.resourcesTypes && formik.errors.resourcesTypes) || ""}
                >
                  <ChoiceToggle
                    value={formik.values.resourcesTypes}
                    onChange={(value) => setFieldValue("resourcesTypes", String(value))}
                    options={[
                      { value: "Propios", label: "Propios" },
                      { value: "Tercero", label: "De un tercero" },
                    ]}
                    disabled={disabled}
                    size="small"
                  />
                </FieldWrapper>
              </Box>
            </AccordionCard>

            <AccordionCard
              index={3}
              title="Declaraciones regulatorias (PEP)"
              summary={pepDone ? "Completado" : "Sin información"}
              status={pepDone ? "COMPLETO" : "PENDIENTE"}
              open={openIdx.has(2)}
              onToggle={() => toggle(2)}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FieldWrapper
                  label="¿El representante legal, propietarios reales o accionistas desempeña o ha desempeñado funciones públicas destacadas en México o en el extranjero, altos puestos ejecutivos en empresas estatales o funciones importantes en partidos políticos, en el último año?"
                  full
                  error={(formik.touched.socioFuncionario && formik.errors.socioFuncionario) || ""}
                >
                  <ChoiceToggle
                    value={formik.values.socioFuncionario}
                    onChange={(value) => responderDeclaracion("socioFuncionario", "peps_socio", String(value))}
                    options={SI_NO}
                    disabled={disabled}
                    size="small"
                  />
                </FieldWrapper>

                {formik.values.socioFuncionario === "true" &&
                  renderListaPeps("peps_socio", "socioFuncionario", "expediente-solicitud-pep-socio")}

                <FieldWrapper
                  label="¿El cónyuge o alguno de los padres, abuelos, hijos, nietos, hermanos, suegros, hijos políticos, abuelos políticos o cuñados de los socios, accionistas o propietarios reales, desempeña o ha desempeñado funciones públicas destacadas en México o en el extranjero, altos puestos ejecutivos en empresas estatales o funciones importantes en partidos políticos?"
                  full
                  error={(formik.touched.conyuge && formik.errors.conyuge) || ""}
                >
                  <ChoiceToggle
                    value={formik.values.conyuge}
                    onChange={(value) => responderDeclaracion("conyuge", "peps_conyuge", String(value))}
                    options={SI_NO}
                    disabled={disabled}
                    size="small"
                  />
                </FieldWrapper>

                {formik.values.conyuge === "true" &&
                  renderListaPeps("peps_conyuge", "conyuge", "expediente-solicitud-pep-familiar", listParentescos)}

                <Callout tone="warn">
                  Las personas políticamente expuestas (PEP) son individuos que ocupan o han ocupado cargos públicos
                  relevantes. Esta información es requerida por regulación.
                </Callout>
              </Box>
            </AccordionCard>
          </Box>

          <StepActions
            onBack={() => navigate(PATH_EXPEDIENTE)}
            onNext={formik.submitForm}
            backLabel="Regresar al Expediente"
            nextLabel="Continuar a referencias"
            nextDisabled={disabled || !contactoDone || !transDone || !pepDone}
            backTestId="expediente-solicitud-back-button"
            nextTestId="expediente-solicitud-continue-button"
          />
        </>
      )}
    </Box>
  );
};
